/**
 * @lodex-pro/web-shared/shell/NavItem — reusable left-nav item component.
 *
 * Phase 9 / Plan 09-03 — used by AppShell to render the navigation list.
 * Active item: a raised, rounded fill with white semibold text (restyle, 2026-10-28).
 * Inactive item: text-secondary color, hover → text-primary + raised bg.
 *
 * 40px height per UI-SPEC Surface Inventory §1.
 * Visible text label is present (not icon-only) so no aria-label needed on the item.
 *
 * Spec: UI-SPEC Surface Inventory §1 (Nav Shell); CLAUDE.md §4 (design tokens).
 */

import * as React from 'react';

export interface NavItemProps {
  /** Human-readable label rendered next to the icon. */
  readonly label: string;
  /** Icon element (Lucide or any React node). */
  readonly icon: React.ReactNode;
  /** Whether this item represents the current active route. */
  readonly active: boolean;
  /** Called when the nav item is clicked. */
  readonly onClick: () => void;
}

/**
 * A single left-nav item with active/inactive visual treatment per UI-SPEC §1.
 *
 * Height: 40px. Padding: 14px horizontal / 8px vertical. Radius 12px.
 * Active: raised background + text-primary, semibold.
 * Inactive: text-secondary, hover → text-primary + raised background.
 */
export function NavItem({ label, icon, active, onClick }: NavItemProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        width: '100%',
        height: '40px',
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '14px',
        paddingRight: '14px',
        border: 'none',
        borderRadius: '12px',
        backgroundColor: active ? 'var(--color-bg-tertiary)' : 'transparent',
        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: '15px',
        fontWeight: active ? 600 : 400,
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
          e.currentTarget.style.color = 'var(--color-text-primary)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'var(--color-text-secondary)';
        }
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
